/**
 * CloudAlertsPage — V2 real-time nurse call alerts page for Cloud 5.0 platform.
 *
 * 4 tabs: Emergency (real-time), Batteries (60s poll), Communication Lost (60s poll), Other (60s poll).
 * Sound notifications for new emergency alarms. Station Mode (fullscreen).
 * NotificationContext integration for real-time cloud emergency alerts.
 *
 * Key differences from 4.0 AlertsPage:
 *   - Uses `activeCloudAlarms` (not `activeAlarms`) from NotificationContext
 *   - Snake_case data schema (event_id, event_datetime_utc, event_category, etc.)
 *   - Batteries + Other tabs from `getdevicemaintenance`; Communication Lost from
 *     `sp_getDeviceOffline` (fetchLostDevices), the same source AlertsPage uses
 *   - No Clear button on maintenance alerts
 *   - No role-based group filtering on emergency alerts
 *   - Emergency table has Description, Accepted By/Time, Device Cleared columns
 *
 * Props: fullscreen — boolean for station mode (initial value)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import { useNotifications } from '../../../contexts/NotificationContext.jsx';
import {
    getSession, getCampusId, getMyCampusId, getCampusList, updateSessionCampus,
    getUserId, getUsername, getSessionId, getCampusConfig, getActiveVendorIdList, setAlertsMobileView,
    getAlertSounds,
} from '../../../services/session.js';
import { sortFunc, setSoundsMute, iAmHigherAuthority } from '../../../libs.jsx';
import { fetchCloudMaintenance, acceptAlert, releaseAlert, finishAlert, acknowledgeAlert, logAcknowledgeLinkTaken } from './api/cloudAlertsApi.js';
import { isAcceptedBy } from '../../../utils/alertActions.js';
import { fetchLostDevices } from './api/alertsApi.js';
import { fetchReasonCodes, fetchHighPriorityEntities } from '../../settings/system-settings/api/cloudSettingsApi.js';

import CloudEmergencyTable from './components/CloudEmergencyTable.jsx';
import CloudMaintenanceTable from './components/CloudMaintenanceTable.jsx';
import LostDevicesTable from './components/LostDevicesTable.jsx';
import AlertSyncIndicator from './components/AlertSyncIndicator.jsx';
import StationModeInfoTip from './components/StationModeInfoTip.jsx';
import MobileModeInfoTip from './components/MobileModeInfoTip.jsx';
import FinishReasonModal from './components/FinishReasonModal.jsx';
import MobileAlertsView from '../../../components/mobile-alerts/MobileAlertsView.jsx';
import MobileReasonDrawer from '../../../components/mobile-alerts/MobileReasonDrawer.jsx';
import { resolveAlarmSound, playSoundUrl, pickAlarmForSound } from '../../../sound/soundMap.js';

import './alerts-page.css';

/** How long to wait for the opening set of alerts before sounds go live. */
const SOUND_PRIME_TIMEOUT_MS = 3000;

const CloudAlertsPage = ({ fullscreen: fullscreenProp }) => {
    // ======================================================================
    // SESSION
    // ======================================================================
    // Held in state (not useMemo) so writes from other pages — e.g. saving
    // portal_afr in CloudSystemSettingsPage — propagate via the
    // `session:updated` event without requiring a full reload.
    const [session, setSessionState] = useState(() => getSession());
    useEffect(() => {
        const handler = () => setSessionState(getSession());
        window.addEventListener('session:updated', handler);
        return () => window.removeEventListener('session:updated', handler);
    }, []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
    // SafelyYou (vendor 17) active for this campus — gates the Acknowledge action
    // (and its Actions column) independently of portal_afr.
    const ackIntegrationActive = useMemo(() => getActiveVendorIdList(campusId).includes(17), [campusId]);
    const isCloud = Boolean(campusConfig?.is_cloud);
    const portalAfr = Boolean(campusConfig?.portal_afr);
    // role_weight > 10 gets the "View Video" GET link (SafelyYou) alongside Acknowledge.
    const canViewVideo = useMemo(() => iAmHigherAuthority(10, session?.role_weight), [session]);

    /* ------------------------------------------------------------------
       Alert interactions require the viewer's OWN campus to match the alert's.

       `campusId` above is the campus being VIEWED. It is not identity: the
       campus selector writes it straight back into the session via
       updateSessionCampus, so a corporate user browsing a sibling campus, or an
       admin impersonating into one, has `acnt` set to that campus and looked
       local to any check that read it.

       `myAcnt` / getMyCampusId() is the campus the account actually logged in
       at. updateSessionCampus never touches it, and ImpersonateBanner restores
       the real session from it, so it survives both campus switching and
       impersonation -- which is what makes it usable as the identity half of
       this comparison.

       Fails CLOSED when myAcnt is missing or 0: a gate that cannot establish
       who the user is must not grant the action. A session predating the field,
       or one whose login returned no CampusId, therefore sees no
       Accept/Release/Finish rather than all of them.
       ------------------------------------------------------------------ */
    const myCampusId = useMemo(() => getMyCampusId(), [session]);
    const isMyCampus = useMemo(
        () => Boolean(myCampusId) && Number(campusId) === Number(myCampusId),
        [campusId, myCampusId],
    );
    /* Both conditions, not either: portal_afr is the campus opting into portal
       interactions at all, isMyCampus is this viewer being entitled to them
       here. Losing either must remove the actions.

       Governs Accept / Release / Finish. ACKNOWLEDGE is gated on isMyCampus
       ALONE, not on this, because acknowledge is the SafelyYou action and hangs
       off ackIntegrationActive rather than portal_afr -- folding portal_afr into
       it would remove acknowledge from a campus that has SafelyYou but portal
       interactions switched off, which is a regression rather than a gate.

       Both views disable their acknowledge button when the handler is absent
       ("disabled={pending || !onAcknowledge}" in CloudEmergencyTable and
       MobileAlertCard), so passing undefined is enough and needs no change in
       either component. View Video is an independent anchor and stays reachable
       -- it is a read that opens SafelyYou, not an action on the alert. */
    const canInteractAlerts = portalAfr && isMyCampus;

    /* Per-row guard for the handlers. Reads the ROW's campus where it carries
       one, so a list that ever mixes campuses is judged row by row rather than
       wholesale. Re-checked inside each handler for the same reason
       acceptBlockedReason is: rows repaint live from the AppSync subscription,
       so what was actionable at render time may not be at click time. */
    const campusInteractionBlocked = useCallback((row) => {
        if (!myCampusId) return true;
        return Number(row?.campus_id ?? campusId) !== Number(myCampusId);
    }, [campusId, myCampusId]);

    const CROSS_CAMPUS_MESSAGE = 'You can only act on alerts at your own campus.';

    // ======================================================================
    // NOTIFICATION CONTEXT (real-time cloud emergency alerts)
    // ======================================================================
    const {
        activeCloudAlarms, isConnected, connectionError, isDataStale,
        registerAlertViewer, unregisterAlertViewer, resubscribeToCurrentCampus,
    } = useNotifications();

    useEffect(() => {
        registerAlertViewer();
        return () => unregisterAlertViewer();
    }, [registerAlertViewer, unregisterAlertViewer]);

    // ======================================================================
    // STATE
    // ======================================================================
    const [activeTab, setActiveTab] = useState('emergency');
    const [fullscreen, setFullscreen] = useState(Boolean(fullscreenProp));
    // Seeded from the session so a refresh keeps the user in whatever mode they chose.
    const [mobileView, setMobileView] = useState(() => Boolean(getSession()?.alertsMobileView));
    const [lowBatData, setLowBatData] = useState([]);
    const [lostData, setLostData] = useState([]);
    const [otherData, setOtherData] = useState([]);

    // Sort state
    const [emergSort, setEmergSort] = useState({ col: 'event_datetime_utc', dir: 'asc' });
    const [maintSort, setMaintSort] = useState({ col: 'event_datetime_utc', dir: 'asc' });
    // Communication Lost uses its own source/shape (fetchLostDevices → LostDevicesTable).
    const [lostSort, setLostSort] = useState({ col: 'last_active', dir: 'asc' });

    // Sound. Which file plays is per-device config delivered on the session; the
    // map is static for the life of the session, so read it once.
    const alertSounds = useMemo(() => getAlertSounds(), []);
    // Per-device sounds are opt-in per campus. Off means every alert plays the
    // original emergency tone, high priority included. Read raw and let
    // customSoundsOn() interpret it — it arrives as a MySQL tinyint.
    const customSounds = campusConfig?.custom_sounds;
    const [soundOff, setSoundOff] = useState(session?.soundsOff || false);
    const alertKeyListRef = useRef([]);
    // Alerts already active when the page opens are the starting picture, not
    // news — they must not sound. The first render pass that carries alerts
    // records their ids silently and lifts this gate (V1 did the same with its
    // `soundInit` flag). Until then nothing plays.
    const soundPrimedRef = useRef(false);

    // Open a campus with no active alerts and there is nothing to prime from, so
    // the gate above would hold forever and swallow the first real alert. Release
    // it on a timer instead. Long enough to cover the provider's initial DynamoDB
    // load, short enough that an alert landing after it is genuinely new.
    useEffect(() => {
        // A campus switch replaces the alert set wholesale, so treat it exactly
        // like arriving on the page: forget the old ids and prime again.
        alertKeyListRef.current = [];
        soundPrimedRef.current = false;
        const timer = setTimeout(() => { soundPrimedRef.current = true; }, SOUND_PRIME_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [campusId]);

    // High-priority event types (config) — Set of event_type_id (as strings) flagged
    // high priority for this campus. Loaded once per campus; used to pin + highlight.
    const [highPriorityTypeIds, setHighPriorityTypeIds] = useState(() => new Set());

    // Portal Accept/Finish
    const [reasonCodes, setReasonCodes] = useState([]);
    const [pendingEventId, setPendingEventId] = useState(null); // eventId with an in-flight accept/finish
    const [finishTarget, setFinishTarget] = useState(null);     // alarm row awaiting reason selection
    const [presetReasonId, setPresetReasonId] = useState(null); // reason to pre-select in the finish prompt (fall carve-out)

    // Toast
    const { toast, showToast, dismissToast } = useToast(6000);

    // Toggle mobile mode and persist the choice to the session so it survives a refresh.
    const toggleMobileView = useCallback(() => {
        setMobileView((prev) => {
            const next = !prev;
            setAlertsMobileView(next);
            return next;
        });
    }, []);

    // ======================================================================
    // EMERGENCY ALERTS (from cloud context — no group filtering for 5.0)
    // ======================================================================
    const filteredEmergency = useMemo(() => {
        return [...(activeCloudAlarms || [])];
    }, [activeCloudAlarms]);

    // Whether an alarm is flagged high priority for this campus. Declared here
    // rather than beside the sort it also feeds, because the sound effect below
    // depends on it — a `const` referenced in an earlier dependency array would
    // be in its temporal dead zone during render.
    const isHighPriority = useCallback(
        (alarm) => highPriorityTypeIds.has((alarm?.event_type_id ?? '').toString()),
        [highPriorityTypeIds],
    );

    // ======================================================================
    // SOUND — play for new alarm IDs
    //
    // WHEN a sound plays: one play per batch of newly-seen alarms, and never for
    // the alarms that were already active when the page opened — arriving on the
    // page is silent, anything that lands afterwards sounds. See soundPrimedRef.
    // WHICH sound is now per-device, configured in the database and delivered on
    // the session (see src/sound/soundMap.js). When a batch contains alarms that
    // map to different sounds, the highest-priority new alarm wins, falling back
    // to the first new one when priority is equal — so the choice doesn't depend
    // on subscription arrival order. High-priority alarms always sound the
    // high-priority file regardless of manufacturer or device type.
    //
    // All of that applies only when the campus has custom_sounds on. With it off
    // the page is back to one tone for every alert (emergency_tone.mp3), which is
    // what campuses heard before per-device sounds existed.
    // ======================================================================
    useEffect(() => {
        const currentKeys = alertKeyListRef.current;
        const newAlarms = [];
        filteredEmergency.forEach((alarm) => {
            if (!currentKeys.includes(alarm.event_id)) {
                newAlarms.push(alarm);
                currentKeys.push(alarm.event_id);
            }
        });
        alertKeyListRef.current = currentKeys;

        // Priming pass: the alerts that were already up when we got here have now
        // been recorded, so nothing about them is new. Play nothing and open the
        // gate. An empty page has nothing to prime from — keep waiting, and let
        // the timer above open the gate if no alerts ever show up.
        if (!soundPrimedRef.current) {
            if (filteredEmergency.length === 0) return;
            soundPrimedRef.current = true;
            return;
        }

        // Keys are still recorded while muted, so unmuting doesn't replay a backlog.
        if (soundOff || newAlarms.length === 0) return;
        const pick = pickAlarmForSound(newAlarms, isHighPriority);
        if (!pick) return;
        const { url } = resolveAlarmSound({
            sounds: alertSounds,
            alarm: pick.alarm,
            isHighPriority: pick.isHighPriority,
            customSounds,
        });
        playSoundUrl(url);
    }, [filteredEmergency, soundOff, alertSounds, isHighPriority, customSounds]);

    // ======================================================================
    // SORTED DATA
    // ======================================================================
    // High-priority alarms always pin to the top (isHighPriority above); the
    // active sort then orders rows within the high-priority and normal groups
    // independently.

    const sortedEmergency = useMemo(() => {
        const sorted = [...filteredEmergency];
        sorted.sort((a, b) => {
            const ap = isHighPriority(a) ? 1 : 0;
            const bp = isHighPriority(b) ? 1 : 0;
            if (ap !== bp) return bp - ap;
            return sortFunc(a, b, emergSort.col, emergSort.dir);
        });
        return sorted;
    }, [filteredEmergency, emergSort, isHighPriority]);

    const sortedLowBat = useMemo(() => {
        const sorted = [...lowBatData];
        sorted.sort((a, b) => sortFunc(a, b, maintSort.col, maintSort.dir));
        return sorted;
    }, [lowBatData, maintSort]);

    const sortedLost = useMemo(() => {
        const sorted = [...lostData];
        sorted.sort((a, b) => sortFunc(a, b, lostSort.col, lostSort.dir));
        return sorted;
    }, [lostData, lostSort]);

    const sortedOther = useMemo(() => {
        const sorted = [...otherData];
        sorted.sort((a, b) => sortFunc(a, b, maintSort.col, maintSort.dir));
        return sorted;
    }, [otherData, maintSort]);

    // ======================================================================
    // POLLING — Maintenance (60s) — all 3 non-emergency tabs from same endpoint
    // ======================================================================
    const loadMaintenance = useCallback(async () => {
        if (!campusId) return;
        try {
            const data = await fetchCloudMaintenance(campusId);
            const tmpBat = [];
            const tmpOther = [];
            (Array.isArray(data) ? data : []).forEach((item) => {
                const cond = (item.condition || '').toLowerCase();
                if (['low battery', 'missing battery'].includes(cond)) {
                    tmpBat.push(item);
                } else if (cond === 'communication lost') {
                    // Communication Lost now has its own source (loadLostDevices) — skip here.
                } else {
                    tmpOther.push(item);
                }
            });
            setLowBatData(tmpBat);
            setOtherData(tmpOther);
        } catch (err) {
            console.error('[CloudAlertsPage] loadMaintenance error:', err);
        }
    }, [campusId]);

    // Communication Lost comes from fetchLostDevices (sp_getDeviceOffline) — the same
    // source AlertsPage uses — not the shared getdevicemaintenance endpoint.
    const loadLostDevices = useCallback(async () => {
        if (!campusId) return;
        try {
            const data = await fetchLostDevices(campusId);
            setLostData(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('[CloudAlertsPage] loadLostDevices error:', err);
        }
    }, [campusId]);

    useEffect(() => { loadMaintenance(); }, [loadMaintenance]);
    useEffect(() => { loadLostDevices(); }, [loadLostDevices]);

    useEffect(() => {
        const maintTimer = setInterval(loadMaintenance, 180000);
        const lostTimer = setInterval(loadLostDevices, 180000);
        return () => { clearInterval(maintTimer); clearInterval(lostTimer); };
    }, [loadMaintenance, loadLostDevices]);

    // ======================================================================
    // HIGH-PRIORITY EVENT TYPES — config data, loaded once per campus.
    // Not real-time: it only changes when an admin edits High Priority settings,
    // so we fetch on mount + campus change (mirrors the reason-codes pattern) and
    // keep a Set of event_type_id for O(1) lookups while alarms stream in.
    // ======================================================================
    useEffect(() => {
        if (!campusId) {
            setHighPriorityTypeIds(new Set());
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchHighPriorityEntities(campusId);
                if (cancelled) return;
                const ids = (Array.isArray(data) ? data : [])
                    .map((r) => (r.event_type_id ?? '').toString())
                    .filter((v) => v !== '' && v !== 'null' && v !== 'undefined');
                setHighPriorityTypeIds(new Set(ids));
            } catch (err) {
                console.error('[CloudAlertsPage] fetchHighPriorityEntities error:', err);
                if (!cancelled) setHighPriorityTypeIds(new Set());
            }
        })();
        return () => { cancelled = true; };
    }, [campusId]);

    // ======================================================================
    // REASON CODES — loaded once per campus while portal_afr is enabled
    // ======================================================================
    useEffect(() => {
        if (!portalAfr || !campusId) {
            setReasonCodes([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchReasonCodes(campusId);
                if (!cancelled) setReasonCodes(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('[CloudAlertsPage] fetchReasonCodes error:', err);
                if (!cancelled) setReasonCodes([]);
            }
        })();
        return () => { cancelled = true; };
    }, [portalAfr, campusId]);

    // ======================================================================
    // ACCEPT / RELEASE / FINISH HANDLERS
    // ======================================================================

    /**
     * Is the session user the caregiver who accepted this alert?
     *
     * Rule: an alert belongs to its accepter — only they may finish it, release it,
     * or acknowledge it when acceptance was required. CloudEmergencyTable hides the
     * buttons on that basis, but the handlers re-check rather than trusting the UI:
     * rows repaint live from the AppSync subscription, so a row can be accepted by
     * someone else between render and click.
     *
     * Identity matches MobileAlertCard: accepted_user_id first, accepted_username
     * as a fallback for rows written before that id existed.
     *
     * DECLARED ABOVE handleAccept BECAUSE IT IS IN ITS DEPENDENCY ARRAY. A dep array
     * is built when its useCallback line runs, so moving this back down throws
     * "Cannot access 'isAcceptedByMe' before initialization" on first render.
     */
    const isAcceptedByMe = useCallback(
        (row) => isAcceptedBy(row, getUserId(), getUsername()),
        [],
    );

    /**
     * The alert this user is already holding, or null.
     *
     * One at a time: a caregiver who has accepted an alert is committed to it, and
     * accepting a second means neither is really being answered. Scans the whole
     * live campus list, not the filtered view, so a held alert that is scrolled off
     * or filtered out still blocks.
     *
     * "Holding" includes an alert that has been accepted and whose device has since
     * cleared but which has not been finished (status Arrived) — the caregiver is
     * still on the hook for it until they finish it, which is the moment it leaves
     * this list.
     */
    const heldAlert = useMemo(
        () => (activeCloudAlarms || []).find((row) => isAcceptedByMe(row)) || null,
        [activeCloudAlarms, isAcceptedByMe],
    );

    /* Why this user cannot accept `row`, or null. Passed to the table and the mobile
       view so the button can explain itself, and re-checked in handleAccept.

       Names the held alert the same way the row does: AlertTypeBadge shows a single
       label — event_type, falling back to event_category — so this uses that same
       pairing rather than joining both with a slash. Joining them produced
       "Emergency / Pendant in Main Office", which is a second naming convention and
       reads as a rendering fault.

       The literal word "alert" after the type is doing real work. Area names here
       are short and often bare numbers ("101", "AL 101"), and without it the
       sentence collapses into "accepted Loitering in 101". */
    const acceptBlockedReason = useCallback((row) => {
        if (!heldAlert) return null;
        if (String(heldAlert.event_id) === String(row?.event_id)) return null;
        const label = heldAlert.event_type || heldAlert.event_category || '';
        const what = label ? `the ${label} alert` : 'an alert';
        const where = heldAlert.area_name ? ` in ${heldAlert.area_name}` : '';
        return `You're already responding to ${what}${where}. Finish or release it before accepting another.`;
    }, [heldAlert]);

    const handleAccept = useCallback(async (row) => {
        if (!row?.event_id || pendingEventId) return;
        /* Re-checked here and not just in the UI: the list repaints live, so this
           user's other alert can arrive between render and click. */
        if (campusInteractionBlocked(row)) {
            showToast('error', CROSS_CAMPUS_MESSAGE);
            return;
        }
        const blocked = acceptBlockedReason(row);
        if (blocked) {
            showToast('error', blocked);
            return;
        }
        setPendingEventId(row.event_id);
        try {
            const [ok, errMsg] = await acceptAlert({
                campusId: row.campus_id ?? campusId,
                eventId: row.event_id,
                userId: getUserId(),
                username: getUsername(),
                displayName: session?.display || getUsername(),
            });
            if (ok) {
                showToast('success', 'Alert accepted.');
                // AppSync subscription will repaint the row naturally.
            } else {
                showToast('error', errMsg || 'Failed to accept alert.');
            }
        } finally {
            setPendingEventId(null);
        }
    }, [pendingEventId, campusId, session, showToast, acceptBlockedReason, campusInteractionBlocked]);

    /**
     * Release an accepted alert back to the floor.
     *
     * Accepter only, by decision: a caregiver can hand back an alert they cannot
     * attend, but nobody can take one out from under someone who is mid-response.
     * Re-checked here for the same live-repaint reason as accept.
     *
     * Only meaningful before the device clears — once ack_datetime_utc is set the
     * alert is on its way to finish, and handing it back would be noise.
     */
    const handleRelease = useCallback(async (row) => {
        if (!row?.event_id || pendingEventId) return;
        if (campusInteractionBlocked(row)) {
            showToast('error', CROSS_CAMPUS_MESSAGE);
            return;
        }
        if (!isAcceptedByMe(row)) {
            showToast('error', `Only ${row.accepted_display || 'the caregiver who accepted this alert'} can release it.`);
            return;
        }
        if (row.ack_datetime_utc) {
            showToast('error', 'This alert has already been cleared by the device and cannot be released.');
            return;
        }
        setPendingEventId(row.event_id);
        try {
            const [ok, errMsg] = await releaseAlert({
                campusId: row.campus_id ?? campusId,
                eventId: row.event_id,
                userId: getUserId(),
            });
            if (ok) {
                showToast('success', 'Alert released. It is available for another caregiver to accept.');
                // The AppSync subscription repaints the row back to Waiting/Overdue.
            } else {
                showToast('error', errMsg || 'Failed to release alert.');
            }
        } finally {
            setPendingEventId(null);
        }
    }, [pendingEventId, campusId, showToast, isAcceptedByMe, campusInteractionBlocked]);

    // Acknowledge (POST to the SafelyYou acknowledge_link). When the alert required
    // acceptance, only its accepter may acknowledge it.
    const handleAcknowledge = useCallback(async (row) => {
        if (!row?.event_id || !row?.acknowledge_link || pendingEventId) return;
        if (campusInteractionBlocked(row)) {
            showToast('error', CROSS_CAMPUS_MESSAGE);
            return;
        }
        if (Boolean(row.accept_required) && !isAcceptedByMe(row)) {
            showToast('error', `Only ${row.accepted_display || 'the caregiver who accepted this alert'} can acknowledge it.`);
            return;
        }
        setPendingEventId(row.event_id);
        try {
            const [ok, errMsg] = await acknowledgeAlert({
                campusId: row.campus_id ?? campusId,
                eventId: row.event_id,
                userId: getUserId(),
                clientId: getSessionId(),
                acknowledgeLink: row.acknowledge_link,
            });
            if (ok) {
                showToast('success', 'Alert has been acknowledged');
            } else {
                showToast('error', errMsg || 'Failed to acknowledge alert.');
            }
        } finally {
            setPendingEventId(null);
        }
    }, [pendingEventId, campusId, showToast, isAcceptedByMe, campusInteractionBlocked]);

    // "View Video" opens the SafelyYou link (GET, new tab) directly. Separately fire
    // a silent background log of who took the link — no toast; console-log non-OK.
    const handleViewVideo = useCallback((row) => {
        if (!row?.event_id) return;
        logAcknowledgeLinkTaken({
            campusId: row.campus_id ?? campusId,
            eventId: row.event_id,
            userId: getUserId(),
            clientId: getSessionId(),
        }).then(([ok, errMsg]) => {
            if (!ok) console.error('[CloudAlertsPage] logAcknowledgeLinkTaken failed:', errMsg);
        });
    }, [campusId]);

    // Core finish — sends the alert to the clear/finish endpoint with a resolved
    // reason + resident. Used both by the reason prompt and the fall carve-out.
    const doFinish = useCallback(async (row, reasonId, residentId) => {
        if (!row?.event_id || !reasonId || pendingEventId) return;
        if (campusInteractionBlocked(row)) {
            showToast('error', CROSS_CAMPUS_MESSAGE);
            return;
        }
        // An alert may only be finished by whoever accepted it — including one that
        // never required acceptance (decided 2026-09-29; see utils/alertActions.js).
        if (!isAcceptedByMe(row)) {
            showToast('error', `Only ${row.accepted_display || 'the caregiver who accepted this alert'} can finish it.`);
            return;
        }
        setPendingEventId(row.event_id);
        try {
            const [ok, errMsg] = await finishAlert({
                campusId: row.campus_id ?? campusId,
                eventId: row.event_id,
                reasonId,
                // single resident → implicit; multiple → user-selected; none → 0
                residentId: residentId ?? row.residents?.[0]?.resident_id ?? 0,
                // Acting user: lets the Lambda verify the finisher is the accepter,
                // and fills the audit log, which recorded user 0 for every portal
                // finish while this went unsent.
                userId: getUserId(),
            });
            if (ok) {
                setFinishTarget(null);
                setPresetReasonId(null);
                showToast('success', 'Alert finished.');
            } else {
                showToast('error', errMsg || 'Failed to finish alert.');
            }
        } finally {
            setPendingEventId(null);
        }
    }, [pendingEventId, campusId, showToast, isAcceptedByMe, campusInteractionBlocked]);

    const handleOpenFinish = useCallback((row) => {
        if (!row?.event_id || pendingEventId) return;

        // Carve-out: SafelyYou fall alerts (event_type_id 40) auto-apply a reason
        // whose description contains "fall" (case-insensitive) when one exists.
        // If the resident is unambiguous (0 or 1 in the area) we finish immediately
        // with no prompt; with multiple residents we still open the prompt so the
        // caregiver attributes the correct resident, but pre-select the fall reason.
        const isType40 = (row.event_type_id ?? '').toString() === '40';
        const fallReason = isType40
            ? (reasonCodes || []).find((rc) => (rc.reason_code_description || '').toLowerCase().includes('fall'))
            : null;

        if (fallReason) {
            const residents = row.residents || [];
            if (residents.length <= 1) {
                doFinish(row, fallReason.cloud_reason_code_id, residents[0]?.resident_id ?? 0);
                return;
            }
            setPresetReasonId(fallReason.cloud_reason_code_id);
            setFinishTarget(row);
            return;
        }

        setPresetReasonId(null);
        setFinishTarget(row);
    }, [pendingEventId, reasonCodes, doFinish]);

    const handleCancelFinish = useCallback(() => {
        setFinishTarget(null);
        setPresetReasonId(null);
    }, []);

    const handleSubmitFinish = useCallback((reasonId, residentId) => {
        if (!finishTarget) return;
        doFinish(finishTarget, reasonId, residentId);
    }, [finishTarget, doFinish]);

    // ======================================================================
    // HANDLERS
    // ======================================================================
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        const cfg = session?.campusData?.[val.toString()];
        // Cloud → on-premise redirect
        if (!cfg?.is_cloud && isCloud) {
            updateSessionCampus(val);
            window.location.href = '/alerts';
            return;
        }
        // On-premise → cloud redirect
        if (cfg?.is_cloud && !isCloud) {
            updateSessionCampus(val);
            window.location.href = '/emergency';
            return;
        }
        setCampusId(val);
        updateSessionCampus(val);
        resubscribeToCurrentCampus();
    }, [session, isCloud, resubscribeToCurrentCampus]);

    const handleSoundToggle = useCallback((e) => {
        const muted = e.target.checked;
        setSoundOff(muted);
        if (session) setSoundsMute(session, muted);
    }, [session]);

    const handleEmergSort = useCallback((col) => {
        setEmergSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    }, []);

    const handleMaintSort = useCallback((col) => {
        setMaintSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    }, []);

    const handleLostSort = useCallback((col) => {
        setLostSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    }, []);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            {!fullscreen ? <NavigationV2 /> : null}
            <div className={fullscreen ? '' : 'nav2-main'}>
                <div className={`na-page${fullscreen ? ' na-page--fullscreen' : ''}`}>
                    {/* Toast */}
                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* Finish Reason Modal — table view only.
                        In mobile view, the drawer renders inside the mobile container so it
                        anchors to that area instead of the viewport. */}
                    {finishTarget && !mobileView ? (
                        <FinishReasonModal
                            row={finishTarget}
                            reasonCodes={reasonCodes}
                            saving={pendingEventId === finishTarget.event_id}
                            onSubmit={handleSubmitFinish}
                            onCancel={handleCancelFinish}
                            defaultReasonId={presetReasonId}
                        />
                    ) : null}

                    {/* Header */}
                    <div className="na-header">
                        <div className="na-header__left">
                            <h1 className="na-header__title">Emergency Alerts</h1>
                            <AlertSyncIndicator isConnected={isConnected} isDataStale={isDataStale} connectionError={connectionError} />
                        </div>
                        <div className="na-header__right">
                            <label className="na-sound-toggle">
                                <input type="checkbox" checked={soundOff} onChange={handleSoundToggle} />
                                <span>Mute Sound</span>
                            </label>
                            {!fullscreen ? (
                                <>
                                    {activeTab === 'emergency' ? (
                                        <MobileModeInfoTip>
                                            <button
                                                className={`na-mobile-toggle rp-btn rp-btn--${mobileView ? 'primary' : 'secondary'}${mobileView ? ' na-mobile-toggle--always' : ''}`}
                                                onClick={toggleMobileView}
                                            >
                                                <i className="fe fe-smartphone" style={{ marginRight: 4 }} />
                                                {mobileView ? 'Exit Mobile' : 'Mobile Mode'}
                                            </button>
                                        </MobileModeInfoTip>
                                    ) : null}
                                    {mobileView ? null : (
                                        <StationModeInfoTip>
                                            <button className="na-station-toggle rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px' }} onClick={() => setFullscreen(true)}>
                                                <i className="fe fe-maximize" style={{ marginRight: 4 }} />Station Mode
                                            </button>
                                        </StationModeInfoTip>
                                    )}
                                    {mobileView ? null : (
                                        <div className="ps-campus-row">
                                            <label htmlFor="ca-campus" className="ps-campus-label">Campus:</label>
                                            {campusList.length > 1 ? (
                                                <select id="ca-campus" className="ps-campus-select" value={campusId} onChange={handleCampusChange}>
                                                    {campusList.map((c) => (<option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>))}
                                                </select>
                                            ) : (
                                                <span className="ps-campus-name">{campusConfig?.name || ''}</span>
                                            )}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <button className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px' }} onClick={() => setFullscreen(false)}>
                                    <i className="fe fe-minimize" style={{ marginRight: 4 }} />Exit Station Mode
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Tab Bar — hidden in mobile mode so the user must exit to switch tabs. */}
                    {mobileView ? null : (
                    <div className="na-tab-bar">
                        <button className={`na-tab${activeTab === 'emergency' ? ' is-active' : ''}`} onClick={() => setActiveTab('emergency')}>
                            Emergency
                            {filteredEmergency.length > 0 ? <span className="na-badge na-badge--danger">{filteredEmergency.length}</span> : null}
                        </button>
                        <button className={`na-tab${activeTab === 'batteries' ? ' is-active' : ''}`} onClick={() => setActiveTab('batteries')}>
                            Batteries
                            {lowBatData.length > 0 ? <span className="na-badge na-badge--info">{lowBatData.length}</span> : null}
                        </button>
                        <button className={`na-tab${activeTab === 'lost' ? ' is-active' : ''}`} onClick={() => setActiveTab('lost')}>
                            Communication Lost
                            {lostData.length > 0 ? <span className="na-badge na-badge--info">{lostData.length}</span> : null}
                        </button>
                        <button className={`na-tab${activeTab === 'other' ? ' is-active' : ''}`} onClick={() => setActiveTab('other')}>
                            Other
                            {otherData.length > 0 ? <span className="na-badge na-badge--info">{otherData.length}</span> : null}
                        </button>
                    </div>
                    )}

                    {/* Content */}
                    <div className={`na-content${mobileView ? ' na-content--mobile' : ''}`}>
                        {activeTab === 'emergency' && mobileView ? (
                            <div className="na-mobile-wrap">
                                <MobileAlertsView
                                    alerts={filteredEmergency}
                                    sessionUserId={getUserId()}
                                    sessionUsername={getUsername()}
                                    onAccept={canInteractAlerts ? handleAccept : undefined}
                                    onRelease={canInteractAlerts ? handleRelease : undefined}
                                    acceptBlockedReason={acceptBlockedReason}
                                    onFinish={canInteractAlerts ? handleOpenFinish : undefined}
                                    onAcknowledge={isMyCampus ? handleAcknowledge : undefined}
                                    onViewVideo={handleViewVideo}
                                    pendingEventId={pendingEventId}
                                    highPriorityTypeIds={highPriorityTypeIds}
                                    ackIntegrationActive={ackIntegrationActive}
                                    canViewVideo={canViewVideo}
                                />
                                {finishTarget ? (
                                    <MobileReasonDrawer
                                        open
                                        row={finishTarget}
                                        reasonCodes={reasonCodes}
                                        saving={pendingEventId === finishTarget.event_id}
                                        onSubmit={handleSubmitFinish}
                                        onCancel={handleCancelFinish}
                                        defaultReasonId={presetReasonId}
                                    />
                                ) : null}
                            </div>
                        ) : null}

                        {activeTab === 'emergency' && !mobileView ? (
                            <CloudEmergencyTable
                                data={sortedEmergency}
                                sortColumn={emergSort.col}
                                sortOrder={emergSort.dir}
                                onSort={handleEmergSort}
                                canInteract={canInteractAlerts}
                                ackIntegrationActive={ackIntegrationActive}
                                canViewVideo={canViewVideo}
                                pendingEventId={pendingEventId}
                                onAccept={handleAccept}
                                onRelease={handleRelease}
                                acceptBlockedReason={acceptBlockedReason}
                                onFinish={handleOpenFinish}
                                onAcknowledge={isMyCampus ? handleAcknowledge : undefined}
                                onViewVideo={handleViewVideo}
                                highPriorityTypeIds={highPriorityTypeIds}
                                sessionUserId={getUserId()}
                                sessionUsername={getUsername()}
                            />
                        ) : null}

                        {activeTab === 'batteries' ? (
                            <CloudMaintenanceTable data={sortedLowBat} sortColumn={maintSort.col} sortOrder={maintSort.dir} onSort={handleMaintSort} />
                        ) : null}

                        {activeTab === 'lost' ? (
                            <LostDevicesTable data={sortedLost} sortColumn={lostSort.col} sortOrder={lostSort.dir} onSort={handleLostSort} />
                        ) : null}

                        {activeTab === 'other' ? (
                            <CloudMaintenanceTable data={sortedOther} sortColumn={maintSort.col} sortOrder={maintSort.dir} onSort={handleMaintSort} />
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
};

export default CloudAlertsPage;
